/**
 * @pdmg/reporting-schema — template document schema.
 *
 * The contract Release 1 is built against. Templates are DECLARATIVE DOCUMENTS the
 * engine interprets, never bespoke components per report format (see scope §7.2).
 * R2's visual builder edits exactly these documents; the data model does not change
 * between releases.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/** Document schema version. Bumped only for breaking changes; drives migration. */
export const SCHEMA_VERSION = 1 as const;

export const Id = z.string().min(1).max(64).regex(/^[a-zA-Z0-9_-]+$/);

/**
 * A binding expression: JSONata, evaluated against the report data model (§4 of the
 * spec) with no host access, no I/O, and a step budget. Always a string here; parsed
 * and statically validated at template-publish time.
 */
/*
 * Deliberately NOT branded. A template must round-trip through JSON — it is stored in
 * Mongo and edited by R2's builder — so a compile-time brand buys nothing at runtime
 * while making the R1 workflow (authoring templates as TypeScript literals) require a
 * cast at every expression. Validity is enforced where it actually matters: the
 * publish-time validator parses every expression against the grammar and checks it
 * against the data model.
 */
export const BindingExpr = z.string().min(1).max(2000);

/** A value that is either a literal or a binding. Literals are the common case. */
export const Bindable = <T extends z.ZodTypeAny>(inner: T) =>
  z.union([inner, z.object({ $expr: BindingExpr })]);

export const Lifecycle = z.enum(['PREFLIGHT', 'INTERIM', 'POSTFLIGHT']);
export type Lifecycle = z.infer<typeof Lifecycle>;

/**
 * How a block behaves when it meets a page boundary.
 *  atomic    — never split; push whole to the next page if it doesn't fit
 *  breakable — may split, repeating any declared header (tables, long lists)
 */
export const FlowBehaviour = z.enum(['atomic', 'breakable']);

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

const Hex = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);

export const ThemeTokens = z.object({
  color: z.object({
    /** Card title bars, header band base. Sampled #001345 from the mockups. */
    primary: Hex,
    /** Title rule, campaign-ID text. Sampled ~#06CDF2. */
    accent: Hex,
    /**
     * The glyph colour inside KPI / identity icon badges. Optional — defaults to `accent`. The
     * streaming-audio mockup draws these icons in navy rather than the accent, so it sets this to
     * `primary`; leaving it unset keeps a report's icons on the accent colour as before.
     */
    iconColor: Hex.optional(),
    /** The bottom-left halftone-dot corner accent colour. Optional — defaults to a light periwinkle. */
    cornerDot: Hex.optional(),
    /** The soft blue glow arcing across the page-header band. Optional — defaults to ~#2E74D6. */
    headerGlow: Hex.optional(),
    surface: Hex,
    surfaceMuted: Hex,
    border: Hex,
    textPrimary: Hex,
    textInverse: Hex,
    /** Optional gradient endpoints for the page header band. */
    headerFrom: Hex.optional(),
    headerTo: Hex.optional(),
    /** The deep third stop of the header gradient. Optional — defaults to ~#002E86. */
    headerDeep: Hex.optional(),
    /**
     * Page background. Cards sit on it in white.
     *
     * The mockups' most basic structural cue: with both white the panels flatten into
     * the sheet and the report reads as one block of content rather than a set of cards.
     */
    page: Hex.optional(),
    /**
     * Border for the contracted-figures strip.
     *
     * The one panel outlined in the accent colour, which is what marks it as the
     * contract rather than another block of delivery numbers.
     */
    stripBorder: Hex.optional(),
  }),
  typography: z.object({
    /** Resolved by the render service against its embedded font set. */
    fontFamily: z.string().min(1),
    baseSizePt: z.number().min(6).max(24),
    scale: z.number().min(1).max(2).default(1.25),
  }),
  page: z.object({
    widthIn: z.number().positive().default(8.5),
    heightIn: z.number().positive().default(11),
    marginIn: z.number().min(0).default(0),
  }),
  branding: z.object({
    /** Asset reference, resolved by the render service. Never a remote URL. */
    logoAssetId: z.string().optional(),
    companyName: z.string().max(200),
    copyrightName: z.string().max(200),
  }),
  /**
   * Icon set for the shared cards: 'legacy' (the inline-SVG glyphs) or 'v2' (the approved raster
   * marks). Default 'legacy'. Only the migrated V2 report templates set 'v2', so every other template
   * keeps the glyphs it was approved with — a template that was not migrated is byte-for-byte unchanged.
   */
  iconStyle: z.enum(['legacy', 'v2']).optional(),
  /** Optional layout knobs that templates tune per report (emitted as CSS vars by themeToCssVars). */
  layout: z.object({
    /** Equal-height floor (inches) for the four summary breakdown cards. Default 1.55in. */
    breakdownMinHeightIn: z.number().positive().optional(),
    /** Page background art. 'waves' = the V2 soft-wave background; omitted = flat page colour. */
    pageBackground: z.enum(['waves']).optional(),
    /** Extra space (inches) above the summary's KPI row, contract strip and both breakdown rows. Default 0. */
    summaryRowSpaceIn: z.number().min(0).max(1).optional(),
    /** Space (inches) above the Campaign Details box on its own page, below the header band. Default 0. */
    detailsTopSpaceIn: z.number().min(0).max(1).optional(),
    /** Extra space (inches) above the audio summary's listening, demographics and Music Genres rows. Default 0. */
    audioRowSpaceIn: z.number().min(0).max(1).optional(),
  }).optional(),
});
export type ThemeTokens = z.infer<typeof ThemeTokens>;

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

/**
 * Repeat a block once per item of a collection.
 * The item is bound to `as` and addressable inside the block's own expressions.
 */
export const RepeatSpec = z.object({
  over: BindingExpr,
  as: z.string().regex(/^[a-z][a-zA-Z0-9]*$/),
  emptyBehaviour: z.enum(['hide', 'placeholder']).default('hide'),
  max: z.number().int().positive().max(5000).optional(),
});

export const BlockInstance: z.ZodType<BlockInstanceT> = z.lazy(() =>
  z.object({
    id: Id,
    /** Must exist in the block registry; validated at publish time. */
    type: z.string().min(1).max(64),

    /** Static configuration. Shape is validated against the block's manifest. */
    props: z.record(z.string(), z.unknown()).default({}),

    /** Data-driven values. Keys must be declared binding slots on the manifest. */
    bindings: z.record(z.string(), BindingExpr).default({}),

    /** Render only when this evaluates truthy. */
    visibleWhen: BindingExpr.optional(),

    /** Render only in these lifecycle stages. Absent = all stages. */
    lifecycle: z.array(Lifecycle).nonempty().optional(),

    repeat: RepeatSpec.optional(),

    /** Sanitised and auto-scoped to this instance. Forces flow: 'atomic'. */
    customCss: z.string().max(20000).optional(),

    /** Nested blocks, for container blocks that declare slots. */
    children: z.array(z.lazy(() => BlockInstance)).optional(),
  })
);

export interface BlockInstanceT {
  id: string;
  type: string;
  props?: Record<string, unknown>;
  bindings?: Record<string, string>;
  visibleWhen?: string;
  lifecycle?: Lifecycle[];
  repeat?: z.infer<typeof RepeatSpec>;
  customCss?: string;
  children?: BlockInstanceT[];
}

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------

/**
 * fixed   — exactly one printed page; overflow is a validation error
 * flowing — content paginates across as many pages as needed
 */
export const PageMode = z.enum(['fixed', 'flowing']);

export const PageDef = z.object({
  id: Id,
  mode: PageMode.default('fixed'),
  /** Authoring label only; never rendered. Page numbers are engine-computed. */
  title: z.string().max(200).optional(),
  visibleWhen: BindingExpr.optional(),
  lifecycle: z.array(Lifecycle).nonempty().optional(),
  /** Repeat the whole page per item — e.g. one details page per ad group. */
  repeat: RepeatSpec.optional(),
  /**
   * Optional per-page chrome header, overriding the document-level `chrome.header` for this page only —
   * for a per-section band title (e.g. "Eblast Summary" vs "Campaign Details"). Engine-placed like the
   * document chrome (C4): it is not authored into the page flow. When absent, the document header is used.
   */
  header: z.array(BlockInstance).optional(),
  blocks: z.array(BlockInstance),
});
export type PageDef = z.infer<typeof PageDef>;

// ---------------------------------------------------------------------------
// Template document
// ---------------------------------------------------------------------------

export const TemplateStatus = z.enum(['draft', 'published', 'archived']);

export const Template = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  id: Id,
  name: z.string().min(1).max(200),

  /** Immutable once published. Editing a published template forks a new version. */
  version: z.number().int().positive(),
  status: TemplateStatus,

  /** null / absent = global template available to every agency. */
  agencyId: z.string().nullable().optional(),

  /** Channels this template is designed for. Used for selection, not rendering. */
  channels: z.array(z.enum(['DISPLAY', 'EMAIL', 'AUDIO', 'VIDEO', 'DOOH'])).nonempty(),

  /** Lifecycle stages this template supports. */
  lifecycle: z.array(Lifecycle).nonempty(),

  theme: ThemeTokens,

  /** Chrome rendered by the engine on every page. Page numbers are NOT authorable. */
  chrome: z.object({
    header: z.array(BlockInstance).default([]),
    footer: z.array(BlockInstance).default([]),
  }).default({ header: [], footer: [] }),

  pages: z.array(PageDef).min(1),

  createdAt: z.string().datetime(),
  createdBy: z.string(),
  publishedAt: z.string().datetime().optional(),
});
export type Template = z.infer<typeof Template>;

// ---------------------------------------------------------------------------
// Block manifests — the registry
// ---------------------------------------------------------------------------

export const PropType = z.enum(['string', 'number', 'boolean', 'enum', 'color', 'asset']);

export const PropSpec = z.object({
  type: PropType,
  required: z.boolean().default(false),
  default: z.unknown().optional(),
  /** For type: 'enum'. */
  values: z.array(z.string()).optional(),
  description: z.string().max(400),
});

export const BindingSlotSpec = z.object({
  /** Expected result type. Enforced statically where inferable, at runtime otherwise. */
  type: z.enum(['string', 'number', 'boolean', 'array', 'object']),
  required: z.boolean().default(false),
  /** Suggested expression offered by the builder. */
  suggestion: z.string().optional(),
  description: z.string().max(400),
});

export const BlockManifest = z.object({
  type: z.string().min(1).max(64),
  label: z.string().min(1).max(80),
  category: z.enum(['chrome', 'identity', 'metric', 'breakdown', 'targeting', 'detail', 'chart', 'content']),
  description: z.string().max(600),

  props: z.record(z.string(), PropSpec).default({}),
  bindings: z.record(z.string(), BindingSlotSpec).default({}),

  /** Container blocks declare named slots for children. */
  slots: z.array(z.string()).optional(),

  flow: FlowBehaviour.default('atomic'),
  /** For breakable blocks: repeat this element on each continuation page. */
  repeatHeaderOnBreak: z.boolean().default(false),

  /** Lifecycle stages the block can render in. */
  lifecycle: z.array(Lifecycle).nonempty(),
  /**
   * Behaviour when a bound value is unavailable because the campaign hasn't
   * delivered yet — the DOOH pre-flight "TBD" case.
   */
  unavailableRendering: z.enum(['tbd', 'zero', 'hide']).default('tbd'),

  /** Nominal height in inches, used by the paginator's measure pass as a hint. */
  nominalHeightIn: z.number().positive().optional(),
});
export type BlockManifest = z.infer<typeof BlockManifest>;

export const BlockRegistry = z.array(BlockManifest);
